import { ReactNode } from "react";

import { FieldArray, useField } from "formik";

import { Button } from "components/elements";
import { Label } from "components/form";
import { Column, Row, GrabsonIcon } from "components/layout";
import { useDragAndDrop } from "hooks/utils";
import { classes } from "utils/classes";
import { getFieldTestId } from "utils/formik";

import styles from "./ListField.scss";

export type ListFieldProps<T> = {
  name: string;

  draggable?: boolean;
  label: string;
  tooltip?: string;
  template: T;
  dataTestId?: string;

  children: (field: {
    name: string;
    disabled: boolean;
    required: boolean;
    dataTestId: string;
  }) => ReactNode;
};

export function ListField<T>({
  name,
  draggable = false,
  label,
  tooltip,
  template,
  dataTestId,
  children,
}: ListFieldProps<T>) {
  const [, { value }] = useField<T[] | undefined>(name);
  const {
    state: { isDragOver, isDragging },
    getRowDragProps,
    getDragHandleProps,
  } = useDragAndDrop();
  const testId = dataTestId || getFieldTestId(name);

  return (
    <div className={styles.container} data-test-id={testId}>
      <FieldArray name={name}>
        {({ push, remove, move }) => (
          <>
            <Label text={label} tooltip={tooltip} />
            {value &&
              value.map((_, index) => {
                const dragHandleProps = getDragHandleProps(index);
                const rowDragProps = draggable
                  ? getRowDragProps(index, move)
                  : {};

                return (
                  <Row
                    // eslint-disable-next-line react/no-array-index-key
                    key={index}
                    className={classes(
                      styles.row,
                      isDragOver(index) ? styles.dragOver : undefined,
                      isDragging(index) ? styles.dragging : undefined
                    )}
                    {...rowDragProps}
                  >
                    <Button
                      className={[styles.actionButton, styles.remove].join(" ")}
                      type="plain"
                      dataTestId={`${testId}-remove-button`}
                      onClick={() => remove(index)}
                    >
                      <GrabsonIcon
                        name={"close-filled"}
                        font="micro"
                        size="xl"
                      />
                    </Button>
                    <Column>
                      {children({
                        name: `${name}[${index}]`,
                        disabled: false,
                        /*
                         * <TextField> validation interferes with the schema
                         * validation. Use only the schema validation.
                         */
                        required: false,
                        dataTestId: `${testId}-item`,
                      })}
                    </Column>
                    {draggable && (
                      <div
                        className={styles.dragHandle}
                        draggable
                        data-test-id={`${testId}-drag-handle`}
                        {...dragHandleProps}
                      >
                        <GrabsonIcon
                          name={"dragdrop2"}
                          font="micro"
                          size="normal"
                        />
                      </div>
                    )}
                  </Row>
                );
              })}
            <Row className={draggable ? styles.dragActionRow : ""}>
              <Button
                className={[styles.actionButton, styles.add].join(" ")}
                type="plain"
                dataTestId={`${testId}-add-button`}
                onClick={() => push(template)}
              >
                <GrabsonIcon name={"plus-filled"} font="micro" size="xl" />
              </Button>
              <Column>
                {children({
                  name: "_placeholder",
                  disabled: true,
                  required: false,
                  dataTestId: `${testId}-placeholder`,
                })}
              </Column>
            </Row>
          </>
        )}
      </FieldArray>
    </div>
  );
}
